/**
 * Modal shown when SessionUser.must_change_password is true. The user can
 * not interact with the app until they set a new password (or sign out).
 */

import { useState, type FormEvent } from 'react';
import * as adminApi from '../api/admin';
import { ApiError } from '../api/client';
import { useAuth } from '../hooks/useAuth';

export default function ForceChangePasswordModal() {
  const { user, signOut } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!user?.must_change_password) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (next !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    if (next.length < 12) {
      setErr('New password must be at least 12 characters.');
      return;
    }
    setBusy(true);
    try {
      await adminApi.changeMyPassword(current, next);
      setDone(true);
      // Refresh after a beat so the modal closes naturally — easiest way
      // to re-pull /api/auth/me.
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'failed';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <span className="modal-title">Set a new password</span>
        </div>
        <form className="modal-body" onSubmit={submit}>
          <div style={{ fontSize: 12.5, color: 'var(--ink2)', marginBottom: 10 }}>
            An administrator set your initial password. Pick a new one before
            you continue. At least 12 characters.
          </div>
          <div className="field">
            <label className="field-label">Current (temporary) password</label>
            <input
              className="field-input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label className="field-label">New password</label>
            <input
              className="field-input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={12}
            />
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label className="field-label">Confirm</label>
            <input
              className="field-input"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={12}
            />
          </div>
          {err && (
            <div className="login-error" style={{ marginTop: 10 }}>
              {err}
            </div>
          )}
          {done && (
            <div style={{ color: 'var(--sage)', fontSize: 13, marginTop: 10 }}>
              Updated. Reloading…
            </div>
          )}
        </form>
        <div className="modal-footer">
          <button className="btn btn-white" type="button" onClick={() => signOut()}>
            Sign out
          </button>
          <button className="btn btn-primary" type="submit" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save & continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
